import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Save, 
  Calculator, 
  FileText, 
  Download,
  Plus,
  Trash2,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Building,
  Coins,
  BarChart3,
  Shield,
  RefreshCw,
  Loader2,
  Search,
  Import,
  Zap
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DevelopmentAppraisalDetailPage() {
  const { id } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [appraisal, setAppraisal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [activeTab, setActiveTab] = useState('site');
  
  // Local state for editable fields
  const [siteInputs, setSiteInputs] = useState({});
  const [proposedScheme, setProposedScheme] = useState({ units: [], scheme_description: '' });
  const [costInputs, setCostInputs] = useState({});
  const [reportMeta, setReportMeta] = useState({});
  const [toggles, setToggles] = useState({ cpr35_mode: false, acquisition_mode: false });
  
  // Comparables state
  const [comparables, setComparables] = useState([]);
  const [comparableStats, setComparableStats] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [newComparable, setNewComparable] = useState({
    address: '', property_type: '', bedrooms: 0, size_sqm: 0, sale_price: 0, sale_date: ''
  });

  useEffect(() => {
    fetchAppraisal();
  }, [id]);

  useEffect(() => {
    if (appraisal?.comparables) {
      setComparables(appraisal.comparables);
      fetchComparableStats();
    }
  }, [appraisal]);

  const fetchComparableStats = async () => {
    try {
      const response = await axios.get(`${API}/development-appraisal/appraisals/${id}/comparables/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComparableStats(response.data);
    } catch (error) {
      console.error('Failed to fetch comparable stats:', error);
    }
  };

  const fetchAppraisal = async () => {
    try {
      const response = await axios.get(`${API}/development-appraisal/appraisals/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setAppraisal(data);
      
      // Initialize local state
      setSiteInputs(data.site_inputs || {});
      setProposedScheme(data.proposed_scheme || { units: [], scheme_description: '' });
      setCostInputs(data.cost_inputs || getDefaultCostInputs());
      setReportMeta({
        client_name: data.client_name || '',
        client_address: data.client_address || '',
        valuer_name: data.valuer_name || '',
        valuer_qualification: data.valuer_qualification || 'MRICS',
        valuer_company: data.valuer_company || '',
        rics_number: data.rics_number || '',
        report_reference: data.report_reference || '',
      });
      setToggles({
        cpr35_mode: data.cpr35_mode || false,
        acquisition_mode: data.acquisition_mode || false,
      });
    } catch (error) {
      console.error('Failed to fetch appraisal:', error);
      toast.error('Failed to load appraisal');
    } finally {
      setLoading(false);
    }
  };

  const getDefaultCostInputs = () => ({
    build_cost_per_sqm: 1500,
    external_works_pct: 15,
    professional_fees_pct: 12,
    warranty_pct: 3,
    contingency_pct: 10,
    cil_per_sqm: 0,
    mayoral_cil_per_sqm: 0,
    s106_contribution: 0,
    marketing_pct: 2.5,
    sales_legal_pct: 0.5,
    debt_funding_pct: 70,
    interest_rate_pct: 8,
    arrangement_fee_pct: 2,
    exit_fee_pct: 1,
    project_months: 18,
    developer_profit_pct: 15,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/development-appraisal/appraisals/${id}`, {
        site_inputs: siteInputs,
        proposed_scheme: proposedScheme,
        cost_inputs: costInputs,
        ...reportMeta,
        ...toggles,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Appraisal saved');
      fetchAppraisal();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save appraisal');
    } finally {
      setSaving(false);
    }
  };

  const handleCalculateAll = async () => {
    // First save, then calculate
    setSaving(true);
    setCalculating(true);
    try {
      // Save first
      await axios.put(`${API}/development-appraisal/appraisals/${id}`, {
        site_inputs: siteInputs,
        proposed_scheme: proposedScheme,
        cost_inputs: costInputs,
        ...reportMeta,
        ...toggles,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then calculate
      const response = await axios.post(
        `${API}/development-appraisal/appraisals/${id}/calculate-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Calculations complete');
      fetchAppraisal();
      setActiveTab('results');
    } catch (error) {
      console.error('Failed to calculate:', error);
      toast.error('Failed to run calculations');
    } finally {
      setSaving(false);
      setCalculating(false);
    }
  };

  const handleGenerateReport = async (format) => {
    try {
      const response = await axios.post(
        `${API}/development-appraisal/appraisals/${id}/generate-report`,
        { format, is_draft: appraisal?.status === 'draft' },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { 
        type: format === 'docx' 
          ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          : 'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Development_Appraisal_${id.slice(0, 8)}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${format.toUpperCase()} report downloaded`);
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate report');
    }
  };

  // Unit management
  const addUnit = () => {
    setProposedScheme(prev => ({
      ...prev,
      units: [...prev.units, {
        id: Date.now().toString(),
        unit_type: '',
        quantity: 1,
        avg_size_sqm: 0,
        price_per_sqm: 0,
      }]
    }));
  };

  const updateUnit = (index, field, value) => {
    setProposedScheme(prev => {
      const newUnits = [...prev.units];
      newUnits[index] = { ...newUnits[index], [field]: value };
      return { ...prev, units: newUnits };
    });
  };

  const removeUnit = (index) => {
    setProposedScheme(prev => ({
      ...prev,
      units: prev.units.filter((_, i) => i !== index)
    }));
  };

  // Comparable management
  const searchComparables = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const response = await axios.get(`${API}/development-appraisal/comparables/search`, {
        params: { q: searchQuery, limit: 10 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.comparables || []);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const addComparable = async () => {
    if (!newComparable.address || !newComparable.sale_price) {
      toast.error('Address and sale price are required');
      return;
    }
    try {
      const response = await axios.post(`${API}/development-appraisal/appraisals/${id}/comparables`, newComparable, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComparables(prev => [...prev, response.data.comparable]);
      setNewComparable({ address: '', property_type: '', bedrooms: 0, size_sqm: 0, sale_price: 0, sale_date: '' });
      toast.success('Comparable added');
      fetchComparableStats();
    } catch (error) {
      console.error('Failed to add comparable:', error);
      toast.error('Failed to add comparable');
    }
  };

  const importComparable = async (comp) => {
    try {
      const importData = {
        address: comp.address,
        property_type: comp.property_type,
        bedrooms: comp.bedrooms,
        size_sqm: comp.floor_area_sqm,
        sale_price: comp.sale_price,
        sale_date: comp.sale_date,
        source: 'Imported from inspection'
      };
      const response = await axios.post(`${API}/development-appraisal/appraisals/${id}/comparables`, importData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComparables(prev => [...prev, response.data.comparable]);
      toast.success('Comparable imported');
      fetchComparableStats();
    } catch (error) {
      console.error('Failed to import comparable:', error);
      toast.error('Failed to import comparable');
    }
  };

  const removeComparable = async (compId) => {
    try {
      await axios.delete(`${API}/development-appraisal/appraisals/${id}/comparables/${compId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComparables(prev => prev.filter(c => c.id !== compId));
      toast.success('Comparable removed');
      fetchComparableStats();
    } catch (error) {
      console.error('Failed to remove comparable:', error);
      toast.error('Failed to remove comparable');
    }
  };

  const applyComparablePricing = async (adjustmentPct = 0, overwriteAll = false) => {
    try {
      const response = await axios.post(`${API}/development-appraisal/appraisals/${id}/apply-comparable-pricing`, {
        adjustment_pct: adjustmentPct,
        overwrite_all: overwriteAll
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Applied £${response.data.applied_price.toLocaleString()}/m² to scheme units`);
      fetchAppraisal(); // Refresh to get updated scheme
    } catch (error) {
      console.error('Failed to apply pricing:', error);
      toast.error(error.response?.data?.detail || 'Failed to apply pricing');
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '—';
    return `£${value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '—';
    return `${value.toFixed(1)}%`;
  };

  const getViabilityColor = (rating) => {
    const colors = {
      strong: 'text-green-600 bg-green-50',
      moderate: 'text-amber-600 bg-amber-50',
      weak: 'text-orange-600 bg-orange-50',
      unviable: 'text-red-600 bg-red-50',
    };
    return colors[rating] || colors.moderate;
  };

  const getRiskColor = (band) => {
    const colors = {
      low: 'text-green-600 bg-green-50',
      moderate: 'text-amber-600 bg-amber-50',
      elevated: 'text-orange-600 bg-orange-50',
      speculative: 'text-red-600 bg-red-50',
    };
    return colors[band] || colors.moderate;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
        </div>
      </Layout>
    );
  }

  if (!appraisal) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Appraisal not found</p>
          <Button onClick={() => navigate('/development-appraisals')} className="mt-4">
            Back to Appraisals
          </Button>
        </div>
      </Layout>
    );
  }

  const rlv = appraisal.residual_land_value || {};
  const sensitivity = appraisal.sensitivity_analysis || {};
  const risk = appraisal.risk_assessment || {};
  const calculatedCosts = appraisal.calculated_costs || {};

  return (
    <Layout>
      <div className="space-y-6" data-testid="development-appraisal-detail">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/development-appraisals')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {appraisal.report_reference || 'Development Appraisal'}
              </h1>
              <p className="text-slate-600">{siteInputs.address || 'No address set'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={handleSave}
              disabled={saving}
              data-testid="save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
            <Button 
              onClick={handleCalculateAll}
              disabled={calculating}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="calculate-btn"
            >
              {calculating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Calculator className="h-4 w-4 mr-2" />
              )}
              Calculate All
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleGenerateReport('docx')}
              data-testid="export-docx-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              DOCX
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleGenerateReport('pdf')}
              data-testid="export-pdf-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-600">Gross Development Value</p>
              <p className="text-2xl font-bold text-blue-700" data-testid="gdv-value">
                {formatCurrency(rlv.gross_development_value)}
              </p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-teal-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-600">Residual Land Value</p>
              <p className="text-2xl font-bold text-teal-700" data-testid="rlv-value">
                {formatCurrency(rlv.rlv_rounded)}
              </p>
            </CardContent>
          </Card>
          <Card className={`border-l-4 ${sensitivity.viability_rating === 'strong' ? 'border-l-green-500' : sensitivity.viability_rating === 'weak' ? 'border-l-red-500' : 'border-l-amber-500'}`}>
            <CardContent className="p-4">
              <p className="text-sm text-slate-600">Viability</p>
              <p className={`text-xl font-bold capitalize ${getViabilityColor(sensitivity.viability_rating)?.split(' ')[0]}`} data-testid="viability-rating">
                {sensitivity.viability_rating || '—'}
              </p>
            </CardContent>
          </Card>
          <Card className={`border-l-4 ${risk.risk_band === 'low' ? 'border-l-green-500' : risk.risk_band === 'speculative' ? 'border-l-red-500' : 'border-l-amber-500'}`}>
            <CardContent className="p-4">
              <p className="text-sm text-slate-600">Risk Band</p>
              <p className={`text-xl font-bold capitalize ${getRiskColor(risk.risk_band)?.split(' ')[0]}`} data-testid="risk-band">
                {risk.risk_band || '—'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-7 w-full max-w-5xl">
            <TabsTrigger value="site" data-testid="tab-site">
              <Building className="h-4 w-4 mr-2" />
              Site
            </TabsTrigger>
            <TabsTrigger value="scheme" data-testid="tab-scheme">
              <Building className="h-4 w-4 mr-2" />
              Scheme
            </TabsTrigger>
            <TabsTrigger value="comparables" data-testid="tab-comparables">
              <Search className="h-4 w-4 mr-2" />
              Comps
            </TabsTrigger>
            <TabsTrigger value="costs" data-testid="tab-costs">
              <Coins className="h-4 w-4 mr-2" />
              Costs
            </TabsTrigger>
            <TabsTrigger value="results" data-testid="tab-results">
              <Calculator className="h-4 w-4 mr-2" />
              Results
            </TabsTrigger>
            <TabsTrigger value="sensitivity" data-testid="tab-sensitivity">
              <BarChart3 className="h-4 w-4 mr-2" />
              Sensitivity
            </TabsTrigger>
            <TabsTrigger value="report" data-testid="tab-report">
              <FileText className="h-4 w-4 mr-2" />
              Report
            </TabsTrigger>
          </TabsList>

          {/* Site Tab */}
          <TabsContent value="site" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Site Information</CardTitle>
                <CardDescription>Enter the site details and characteristics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Site Address</Label>
                    <Textarea
                      value={siteInputs.address || ''}
                      onChange={(e) => setSiteInputs(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Full site address"
                      rows={3}
                      data-testid="input-address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Local Planning Authority</Label>
                    <Input
                      value={siteInputs.local_planning_authority || ''}
                      onChange={(e) => setSiteInputs(prev => ({ ...prev, local_planning_authority: e.target.value }))}
                      placeholder="e.g., London Borough of Camden"
                      data-testid="input-lpa"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label>Site Area (m²)</Label>
                    <Input
                      type="number"
                      value={siteInputs.site_area_sqm || ''}
                      onChange={(e) => setSiteInputs(prev => ({ ...prev, site_area_sqm: parseFloat(e.target.value) || 0 }))}
                      placeholder="0"
                      data-testid="input-site-area"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Planning Status</Label>
                    <Select
                      value={siteInputs.planning_status || 'unconsented'}
                      onValueChange={(value) => setSiteInputs(prev => ({ ...prev, planning_status: value }))}
                    >
                      <SelectTrigger data-testid="select-planning-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unconsented">Unconsented</SelectItem>
                        <SelectItem value="outline">Outline Permission</SelectItem>
                        <SelectItem value="full">Full Permission</SelectItem>
                        <SelectItem value="permitted_development">Permitted Development</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Ownership Status</Label>
                    <Select
                      value={siteInputs.ownership_status || 'owned'}
                      onValueChange={(value) => setSiteInputs(prev => ({ ...prev, ownership_status: value }))}
                    >
                      <SelectTrigger data-testid="select-ownership">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owned">Owned</SelectItem>
                        <SelectItem value="acquisition">Acquisition</SelectItem>
                        <SelectItem value="option">Option</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Acquisition fields if applicable */}
                {(siteInputs.ownership_status === 'acquisition' || toggles.acquisition_mode) && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4 bg-amber-50 rounded-lg">
                    <div className="space-y-2">
                      <Label>Acquisition Price (£)</Label>
                      <Input
                        type="number"
                        value={siteInputs.acquisition_price || ''}
                        onChange={(e) => setSiteInputs(prev => ({ ...prev, acquisition_price: parseFloat(e.target.value) || 0 }))}
                        placeholder="0"
                        data-testid="input-acquisition-price"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>SDLT Rate (%)</Label>
                      <Input
                        type="number"
                        value={siteInputs.sdlt_rate || 5}
                        onChange={(e) => setSiteInputs(prev => ({ ...prev, sdlt_rate: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-sdlt-rate"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Legal Fees (%)</Label>
                      <Input
                        type="number"
                        value={siteInputs.acquisition_legal_fees || 1.5}
                        onChange={(e) => setSiteInputs(prev => ({ ...prev, acquisition_legal_fees: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-legal-fees"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Site Description</Label>
                  <Textarea
                    value={siteInputs.site_description || ''}
                    onChange={(e) => setSiteInputs(prev => ({ ...prev, site_description: e.target.value }))}
                    placeholder="Describe the site characteristics, access, topography..."
                    rows={4}
                    data-testid="input-site-description"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scheme Tab */}
          <TabsContent value="scheme" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Proposed Scheme</span>
                  <Button onClick={addUnit} size="sm" data-testid="add-unit-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Unit Type
                  </Button>
                </CardTitle>
                <CardDescription>Define the proposed development units</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Scheme Description</Label>
                  <Textarea
                    value={proposedScheme.scheme_description || ''}
                    onChange={(e) => setProposedScheme(prev => ({ ...prev, scheme_description: e.target.value }))}
                    placeholder="Brief description of the proposed development..."
                    rows={3}
                    data-testid="input-scheme-description"
                  />
                </div>

                {/* Units Table */}
                <Table data-testid="units-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Unit Type</TableHead>
                      <TableHead className="w-24">Qty</TableHead>
                      <TableHead className="w-32">Avg Size (m²)</TableHead>
                      <TableHead className="w-32">£/m²</TableHead>
                      <TableHead className="w-32">Unit Value</TableHead>
                      <TableHead className="w-32">Total Value</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {proposedScheme.units?.map((unit, index) => {
                      const unitValue = (unit.avg_size_sqm || 0) * (unit.price_per_sqm || 0);
                      const totalValue = (unit.quantity || 0) * unitValue;
                      return (
                        <TableRow key={unit.id || index}>
                          <TableCell>
                            <Input
                              value={unit.unit_type || ''}
                              onChange={(e) => updateUnit(index, 'unit_type', e.target.value)}
                              placeholder="e.g., 3-bed semi"
                              data-testid={`unit-type-${index}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={unit.quantity || ''}
                              onChange={(e) => updateUnit(index, 'quantity', parseInt(e.target.value) || 0)}
                              min="1"
                              data-testid={`unit-qty-${index}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={unit.avg_size_sqm || ''}
                              onChange={(e) => updateUnit(index, 'avg_size_sqm', parseFloat(e.target.value) || 0)}
                              data-testid={`unit-size-${index}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={unit.price_per_sqm || ''}
                              onChange={(e) => updateUnit(index, 'price_per_sqm', parseFloat(e.target.value) || 0)}
                              data-testid={`unit-price-${index}`}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-slate-600">
                            {formatCurrency(unitValue)}
                          </TableCell>
                          <TableCell className="font-mono font-semibold">
                            {formatCurrency(totalValue)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeUnit(index)}
                              data-testid={`remove-unit-${index}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {proposedScheme.units?.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                          No units added. Click "Add Unit Type" to begin.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Totals */}
                {proposedScheme.units?.length > 0 && (
                  <div className="flex justify-end">
                    <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                      <div className="flex justify-between gap-8">
                        <span className="text-slate-600">Total Units:</span>
                        <span className="font-semibold">
                          {proposedScheme.units.reduce((sum, u) => sum + (u.quantity || 0), 0)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-8">
                        <span className="text-slate-600">Total GIA:</span>
                        <span className="font-semibold">
                          {proposedScheme.units.reduce((sum, u) => sum + ((u.quantity || 0) * (u.avg_size_sqm || 0)), 0).toLocaleString()} m²
                        </span>
                      </div>
                      <div className="flex justify-between gap-8 text-lg border-t pt-2">
                        <span className="text-slate-600">Calculated GDV:</span>
                        <span className="font-bold text-blue-700">
                          {formatCurrency(proposedScheme.units.reduce((sum, u) => {
                            const unitVal = (u.avg_size_sqm || 0) * (u.price_per_sqm || 0);
                            return sum + ((u.quantity || 0) * unitVal);
                          }, 0))}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* GDV Override */}
                <div className="p-4 bg-amber-50 rounded-lg space-y-2">
                  <div className="flex items-center gap-4">
                    <div className="flex-1 space-y-2">
                      <Label>GDV Override (optional)</Label>
                      <Input
                        type="number"
                        value={proposedScheme.gdv_override || ''}
                        onChange={(e) => setProposedScheme(prev => ({ ...prev, gdv_override: parseFloat(e.target.value) || null }))}
                        placeholder="Leave blank to use calculated GDV"
                        data-testid="input-gdv-override"
                      />
                    </div>
                    <div className="flex-1 space-y-2">
                      <Label>Override Reason</Label>
                      <Input
                        value={proposedScheme.gdv_override_reason || ''}
                        onChange={(e) => setProposedScheme(prev => ({ ...prev, gdv_override_reason: e.target.value }))}
                        placeholder="Reason for override..."
                        data-testid="input-gdv-override-reason"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Comparables Tab */}
          <TabsContent value="comparables" className="mt-6">
            <div className="space-y-6">
              {/* Stats Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-purple-500">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-600">Comparables Count</p>
                    <p className="text-2xl font-bold">{comparableStats?.count || 0}</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-indigo-500">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-600">Avg £/m²</p>
                    <p className="text-2xl font-bold text-indigo-700">
                      {comparableStats?.avg_price_per_sqm ? formatCurrency(comparableStats.avg_price_per_sqm) : '—'}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-600">Min £/m²</p>
                    <p className="text-lg font-semibold text-green-700">
                      {comparableStats?.min_price_per_sqm ? formatCurrency(comparableStats.min_price_per_sqm) : '—'}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-red-500">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-600">Max £/m²</p>
                    <p className="text-lg font-semibold text-red-700">
                      {comparableStats?.max_price_per_sqm ? formatCurrency(comparableStats.max_price_per_sqm) : '—'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Apply to Scheme */}
              {comparableStats?.count > 0 && (
                <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-indigo-900">Apply Comparable Pricing to Scheme</h4>
                        <p className="text-sm text-indigo-700">
                          Average from {comparableStats.count} comparables: <strong>{formatCurrency(comparableStats.avg_price_per_sqm)}/m²</strong>
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          onClick={() => applyComparablePricing(0, false)}
                          data-testid="apply-pricing-empty"
                        >
                          Fill Empty Only
                        </Button>
                        <Button 
                          onClick={() => applyComparablePricing(0, true)}
                          className="bg-indigo-600 hover:bg-indigo-700"
                          data-testid="apply-pricing-all"
                        >
                          <Zap className="h-4 w-4 mr-2" />
                          Apply to All Units
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Search Existing Comparables */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5" />
                    Import from Inspections
                  </CardTitle>
                  <CardDescription>Search and import comparables from existing inspections</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by address or property type..."
                      onKeyPress={(e) => e.key === 'Enter' && searchComparables()}
                      data-testid="search-comparables-input"
                    />
                    <Button onClick={searchComparables} disabled={searching} data-testid="search-comparables-btn">
                      {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </div>

                  {searchResults.length > 0 && (
                    <div className="border rounded-lg divide-y max-h-64 overflow-auto">
                      {searchResults.map((comp) => (
                        <div key={comp.id} className="p-3 flex items-center justify-between hover:bg-slate-50">
                          <div>
                            <p className="font-medium text-sm">{comp.address}</p>
                            <p className="text-xs text-slate-500">
                              {comp.property_type} • {comp.floor_area_sqm}m² • {formatCurrency(comp.sale_price)}
                              {comp.price_per_sqm && ` (${formatCurrency(comp.price_per_sqm)}/m²)`}
                            </p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => importComparable(comp)}
                            data-testid={`import-comp-${comp.id}`}
                          >
                            <Import className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Add Manual Comparable */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5" />
                    Add Comparable Manually
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Address</Label>
                      <Input
                        value={newComparable.address}
                        onChange={(e) => setNewComparable(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="Property address"
                        data-testid="new-comp-address"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Property Type</Label>
                      <Input
                        value={newComparable.property_type}
                        onChange={(e) => setNewComparable(prev => ({ ...prev, property_type: e.target.value }))}
                        placeholder="e.g., Semi-detached"
                        data-testid="new-comp-type"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Bedrooms</Label>
                      <Input
                        type="number"
                        value={newComparable.bedrooms || ''}
                        onChange={(e) => setNewComparable(prev => ({ ...prev, bedrooms: parseInt(e.target.value) || 0 }))}
                        data-testid="new-comp-beds"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Size (m²)</Label>
                      <Input
                        type="number"
                        value={newComparable.size_sqm || ''}
                        onChange={(e) => setNewComparable(prev => ({ ...prev, size_sqm: parseFloat(e.target.value) || 0 }))}
                        data-testid="new-comp-size"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sale Price (£)</Label>
                      <Input
                        type="number"
                        value={newComparable.sale_price || ''}
                        onChange={(e) => setNewComparable(prev => ({ ...prev, sale_price: parseFloat(e.target.value) || 0 }))}
                        data-testid="new-comp-price"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sale Date</Label>
                      <Input
                        type="date"
                        value={newComparable.sale_date || ''}
                        onChange={(e) => setNewComparable(prev => ({ ...prev, sale_date: e.target.value }))}
                        data-testid="new-comp-date"
                      />
                    </div>
                  </div>
                  <Button onClick={addComparable} className="mt-4" data-testid="add-comp-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Comparable
                  </Button>
                </CardContent>
              </Card>

              {/* Comparables Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Linked Comparables ({comparables.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {comparables.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Search className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>No comparables linked yet.</p>
                      <p className="text-sm">Search existing inspections or add manually above.</p>
                    </div>
                  ) : (
                    <Table data-testid="comparables-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Address</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Size (m²)</TableHead>
                          <TableHead className="text-right">Sale Price</TableHead>
                          <TableHead className="text-right">£/m²</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {comparables.map((comp) => (
                          <TableRow key={comp.id}>
                            <TableCell className="font-medium max-w-[200px] truncate">{comp.address}</TableCell>
                            <TableCell>{comp.property_type || '—'}</TableCell>
                            <TableCell className="text-right font-mono">{comp.size_sqm?.toLocaleString() || '—'}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(comp.sale_price)}</TableCell>
                            <TableCell className="text-right font-mono font-semibold text-indigo-700">
                              {formatCurrency(comp.price_per_sqm)}
                            </TableCell>
                            <TableCell className="text-slate-500 text-sm">{comp.sale_date || '—'}</TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeComparable(comp.id)}
                                data-testid={`remove-comp-${comp.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Pricing by Type */}
              {comparableStats?.by_property_type && Object.keys(comparableStats.by_property_type).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Pricing by Property Type</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {Object.entries(comparableStats.by_property_type).map(([type, data]) => (
                        <div key={type} className="p-4 bg-slate-50 rounded-lg">
                          <h4 className="font-semibold text-slate-700">{type}</h4>
                          <p className="text-2xl font-bold text-indigo-700">{formatCurrency(data.avg_price_per_sqm)}/m²</p>
                          <p className="text-sm text-slate-500">{data.count} comparable{data.count !== 1 ? 's' : ''}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Costs Tab */}
          <TabsContent value="costs" className="mt-6">
            <div className="space-y-6">
              {/* Construction Costs */}
              <Card>
                <CardHeader>
                  <CardTitle>Construction Costs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label>Build Cost (£/m²)</Label>
                      <Input
                        type="number"
                        value={costInputs.build_cost_per_sqm || ''}
                        onChange={(e) => setCostInputs(prev => ({ ...prev, build_cost_per_sqm: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-build-cost"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>External Works (%)</Label>
                      <Input
                        type="number"
                        value={costInputs.external_works_pct || ''}
                        onChange={(e) => setCostInputs(prev => ({ ...prev, external_works_pct: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-external-works"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Professional Fees (%)</Label>
                      <Input
                        type="number"
                        value={costInputs.professional_fees_pct || ''}
                        onChange={(e) => setCostInputs(prev => ({ ...prev, professional_fees_pct: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-prof-fees"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Warranty (%)</Label>
                      <Input
                        type="number"
                        value={costInputs.warranty_pct || ''}
                        onChange={(e) => setCostInputs(prev => ({ ...prev, warranty_pct: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-warranty"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contingency (%)</Label>
                      <Input
                        type="number"
                        value={costInputs.contingency_pct || ''}
                        onChange={(e) => setCostInputs(prev => ({ ...prev, contingency_pct: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-contingency"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Planning Costs */}
              <Card>
                <CardHeader>
                  <CardTitle>Planning & Statutory Costs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label>CIL (£/m²)</Label>
                      <Input
                        type="number"
                        value={costInputs.cil_per_sqm || ''}
                        onChange={(e) => setCostInputs(prev => ({ ...prev, cil_per_sqm: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-cil"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Mayoral CIL (£/m²)</Label>
                      <Input
                        type="number"
                        value={costInputs.mayoral_cil_per_sqm || ''}
                        onChange={(e) => setCostInputs(prev => ({ ...prev, mayoral_cil_per_sqm: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-mayoral-cil"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>S106 Contribution (£)</Label>
                      <Input
                        type="number"
                        value={costInputs.s106_contribution || ''}
                        onChange={(e) => setCostInputs(prev => ({ ...prev, s106_contribution: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-s106"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Marketing & Sales */}
              <Card>
                <CardHeader>
                  <CardTitle>Marketing & Sales</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Marketing (% of GDV)</Label>
                      <Input
                        type="number"
                        value={costInputs.marketing_pct || ''}
                        onChange={(e) => setCostInputs(prev => ({ ...prev, marketing_pct: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-marketing"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sales Legal (% of GDV)</Label>
                      <Input
                        type="number"
                        value={costInputs.sales_legal_pct || ''}
                        onChange={(e) => setCostInputs(prev => ({ ...prev, sales_legal_pct: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-sales-legal"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Finance */}
              <Card>
                <CardHeader>
                  <CardTitle>Finance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label>Debt Funding (%)</Label>
                      <Input
                        type="number"
                        value={costInputs.debt_funding_pct || ''}
                        onChange={(e) => setCostInputs(prev => ({ ...prev, debt_funding_pct: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-debt-funding"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Interest Rate (% p.a.)</Label>
                      <Input
                        type="number"
                        value={costInputs.interest_rate_pct || ''}
                        onChange={(e) => setCostInputs(prev => ({ ...prev, interest_rate_pct: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-interest-rate"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Project Duration (months)</Label>
                      <Input
                        type="number"
                        value={costInputs.project_months || ''}
                        onChange={(e) => setCostInputs(prev => ({ ...prev, project_months: parseInt(e.target.value) || 0 }))}
                        data-testid="input-project-months"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Arrangement Fee (%)</Label>
                      <Input
                        type="number"
                        value={costInputs.arrangement_fee_pct || ''}
                        onChange={(e) => setCostInputs(prev => ({ ...prev, arrangement_fee_pct: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-arrangement-fee"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Exit Fee (%)</Label>
                      <Input
                        type="number"
                        value={costInputs.exit_fee_pct || ''}
                        onChange={(e) => setCostInputs(prev => ({ ...prev, exit_fee_pct: parseFloat(e.target.value) || 0 }))}
                        data-testid="input-exit-fee"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Developer Profit */}
              <Card className="border-teal-200 bg-teal-50">
                <CardHeader>
                  <CardTitle>Developer Profit</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-w-xs">
                    <Label>Profit (% of GDV)</Label>
                    <Input
                      type="number"
                      value={costInputs.developer_profit_pct || ''}
                      onChange={(e) => setCostInputs(prev => ({ ...prev, developer_profit_pct: parseFloat(e.target.value) || 0 }))}
                      className="text-lg font-semibold"
                      data-testid="input-developer-profit"
                    />
                    <p className="text-sm text-slate-600">Industry standard: 15-20% for residential</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results" className="mt-6">
            <div className="space-y-6">
              {/* RLV Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="h-5 w-5" />
                    Residual Land Value Calculation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Gross Development Value</span>
                        <span className="font-semibold">{formatCurrency(rlv.gross_development_value)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Less: Development Costs</span>
                        <span className="text-red-600">({formatCurrency(rlv.total_costs_excl_land)})</span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Less: Developer Profit</span>
                        <span className="text-red-600">({formatCurrency(rlv.developer_profit)})</span>
                      </div>
                      <div className="flex justify-between py-3 bg-teal-50 px-4 rounded-lg">
                        <span className="font-bold text-lg">Residual Land Value</span>
                        <span className="font-bold text-lg text-teal-700">{formatCurrency(rlv.residual_land_value)}</span>
                      </div>
                      <div className="flex justify-between py-3 bg-teal-100 px-4 rounded-lg">
                        <span className="font-bold text-lg">RLV (Rounded)</span>
                        <span className="font-bold text-xl text-teal-800" data-testid="rlv-rounded">{formatCurrency(rlv.rlv_rounded)}</span>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <h4 className="font-semibold text-slate-700">Key Metrics</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <p className="text-sm text-slate-600">RLV per m² (site)</p>
                          <p className="text-xl font-bold">{formatCurrency(rlv.rlv_per_sqm_site)}</p>
                        </div>
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <p className="text-sm text-slate-600">RLV per Unit</p>
                          <p className="text-xl font-bold">{formatCurrency(rlv.rlv_per_unit)}</p>
                        </div>
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <p className="text-sm text-slate-600">Profit on GDV</p>
                          <p className="text-xl font-bold">{formatPercent(rlv.profit_on_gdv_pct)}</p>
                        </div>
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <p className="text-sm text-slate-600">Profit on Cost</p>
                          <p className="text-xl font-bold">{formatPercent(rlv.profit_on_cost_pct)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Cost Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Cost Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <h4 className="font-semibold text-slate-700 mb-3">Construction</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between"><span>Build Cost</span><span>{formatCurrency(calculatedCosts.build_cost)}</span></div>
                        <div className="flex justify-between"><span>External Works</span><span>{formatCurrency(calculatedCosts.external_works)}</span></div>
                        <div className="flex justify-between"><span>Professional Fees</span><span>{formatCurrency(calculatedCosts.professional_fees)}</span></div>
                        <div className="flex justify-between"><span>Warranty</span><span>{formatCurrency(calculatedCosts.warranty_costs)}</span></div>
                        <div className="flex justify-between"><span>Contingency</span><span>{formatCurrency(calculatedCosts.contingency)}</span></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-slate-700 mb-3">Planning & Other</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between"><span>CIL</span><span>{formatCurrency(calculatedCosts.cil_cost)}</span></div>
                        <div className="flex justify-between"><span>Mayoral CIL</span><span>{formatCurrency(calculatedCosts.mayoral_cil)}</span></div>
                        <div className="flex justify-between"><span>S106</span><span>{formatCurrency(calculatedCosts.s106_cost)}</span></div>
                        <div className="flex justify-between"><span>Marketing</span><span>{formatCurrency(calculatedCosts.marketing_cost)}</span></div>
                        <div className="flex justify-between"><span>Sales Legal</span><span>{formatCurrency(calculatedCosts.sales_legal_cost)}</span></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-slate-700 mb-3">Finance</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between"><span>Interest</span><span>{formatCurrency(calculatedCosts.finance_cost)}</span></div>
                        <div className="flex justify-between"><span>Arrangement Fee</span><span>{formatCurrency(calculatedCosts.arrangement_fee)}</span></div>
                        <div className="flex justify-between"><span>Exit Fee</span><span>{formatCurrency(calculatedCosts.exit_fee)}</span></div>
                        <div className="flex justify-between font-semibold pt-2 border-t"><span>Total Finance</span><span>{formatCurrency(calculatedCosts.total_finance)}</span></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-6 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-100 p-4 rounded-lg">
                      <p className="text-sm text-slate-600">Total Dev Cost</p>
                      <p className="text-lg font-bold">{formatCurrency(calculatedCosts.total_development_cost)}</p>
                    </div>
                    <div className="bg-amber-100 p-4 rounded-lg">
                      <p className="text-sm text-slate-600">Developer Profit</p>
                      <p className="text-lg font-bold">{formatCurrency(calculatedCosts.developer_profit)}</p>
                    </div>
                    <div className="bg-slate-200 p-4 rounded-lg">
                      <p className="text-sm text-slate-600">Cost + Profit</p>
                      <p className="text-lg font-bold">{formatCurrency(calculatedCosts.total_cost_incl_profit)}</p>
                    </div>
                    <div className="bg-teal-100 p-4 rounded-lg">
                      <p className="text-sm text-slate-600">All-In Cost</p>
                      <p className="text-lg font-bold">{formatCurrency(calculatedCosts.all_in_cost)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Sensitivity Tab */}
          <TabsContent value="sensitivity" className="mt-6">
            <div className="space-y-6">
              {/* Sensitivity Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Sensitivity Analysis
                  </CardTitle>
                  <CardDescription>
                    Stress testing the appraisal under different scenarios
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table data-testid="sensitivity-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Scenario</TableHead>
                        <TableHead className="text-right">GDV</TableHead>
                        <TableHead className="text-right">Cost</TableHead>
                        <TableHead className="text-right">RLV</TableHead>
                        <TableHead className="text-right">% Change</TableHead>
                        <TableHead className="text-center">Viable</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sensitivity.scenarios?.map((scenario, index) => (
                        <TableRow key={index} className={!scenario.is_viable ? 'bg-red-50' : ''}>
                          <TableCell className="font-medium">{scenario.scenario_name}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(scenario.adjusted_gdv)}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(scenario.adjusted_cost)}</TableCell>
                          <TableCell className={`text-right font-mono font-semibold ${scenario.adjusted_rlv < 0 ? 'text-red-600' : 'text-teal-700'}`}>
                            {formatCurrency(scenario.adjusted_rlv)}
                          </TableCell>
                          <TableCell className={`text-right font-mono ${scenario.rlv_change_pct < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {scenario.rlv_change_pct > 0 ? '+' : ''}{scenario.rlv_change_pct?.toFixed(1)}%
                          </TableCell>
                          <TableCell className="text-center">
                            {scenario.is_viable ? (
                              <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                            ) : (
                              <AlertTriangle className="h-5 w-5 text-red-500 mx-auto" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Viability Assessment */}
              <Card>
                <CardHeader>
                  <CardTitle>Viability Assessment</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className={`p-6 rounded-lg ${getViabilityColor(sensitivity.viability_rating)}`}>
                      <p className="text-sm opacity-75">Rating</p>
                      <p className="text-2xl font-bold capitalize" data-testid="viability-rating-detail">
                        {sensitivity.viability_rating || '—'}
                      </p>
                    </div>
                    <div className="bg-slate-50 p-6 rounded-lg">
                      <p className="text-sm text-slate-600">Viable Scenarios</p>
                      <p className="text-2xl font-bold">
                        {sensitivity.viable_scenarios} / {sensitivity.total_scenarios}
                      </p>
                    </div>
                    <div className="bg-slate-50 p-6 rounded-lg">
                      <p className="text-sm text-slate-600">RLV Range</p>
                      <p className="text-lg font-bold">
                        {formatCurrency(sensitivity.worst_case_rlv)} to {formatCurrency(sensitivity.best_case_rlv)}
                      </p>
                    </div>
                  </div>
                  
                  {sensitivity.viability_commentary && (
                    <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-semibold mb-2">Commentary</h4>
                      <p className="text-slate-700">{sensitivity.viability_commentary}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Risk Assessment */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Risk Assessment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className={`p-6 rounded-lg ${getRiskColor(risk.risk_band)}`}>
                        <p className="text-sm opacity-75">Risk Band</p>
                        <p className="text-2xl font-bold capitalize" data-testid="risk-band-detail">
                          {risk.risk_band || '—'}
                        </p>
                        <p className="text-sm mt-1">Score: {risk.risk_score}/100</p>
                      </div>
                      
                      <div className="mt-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Profit Margin</span>
                          <Badge variant="outline" className="capitalize">{risk.profit_margin_risk}</Badge>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Sensitivity</span>
                          <Badge variant="outline" className="capitalize">{risk.sensitivity_risk}</Badge>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Planning</span>
                          <Badge variant="outline" className="capitalize">{risk.planning_risk}</Badge>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Build Cost</span>
                          <Badge variant="outline" className="capitalize">{risk.build_cost_risk}</Badge>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Market</span>
                          <Badge variant="outline" className="capitalize">{risk.market_risk}</Badge>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      {risk.risk_commentary && (
                        <div className="p-4 bg-slate-50 rounded-lg mb-4">
                          <h4 className="font-semibold mb-2">Commentary</h4>
                          <p className="text-sm text-slate-700">{risk.risk_commentary}</p>
                        </div>
                      )}
                      
                      {risk.recommendations?.length > 0 && (
                        <div className="p-4 bg-amber-50 rounded-lg">
                          <h4 className="font-semibold mb-2">Recommendations</h4>
                          <ul className="space-y-1">
                            {risk.recommendations.map((rec, i) => (
                              <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                                <span className="text-amber-500">•</span>
                                {rec}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Report Tab */}
          <TabsContent value="report" className="mt-6">
            <div className="space-y-6">
              {/* Report Metadata */}
              <Card>
                <CardHeader>
                  <CardTitle>Report Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Client Name</Label>
                      <Input
                        value={reportMeta.client_name || ''}
                        onChange={(e) => setReportMeta(prev => ({ ...prev, client_name: e.target.value }))}
                        data-testid="input-report-client"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Client Address</Label>
                      <Input
                        value={reportMeta.client_address || ''}
                        onChange={(e) => setReportMeta(prev => ({ ...prev, client_address: e.target.value }))}
                        data-testid="input-report-client-address"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Report Reference</Label>
                      <Input
                        value={reportMeta.report_reference || ''}
                        onChange={(e) => setReportMeta(prev => ({ ...prev, report_reference: e.target.value }))}
                        data-testid="input-report-reference"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Valuer Details */}
              <Card>
                <CardHeader>
                  <CardTitle>Valuer Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Valuer Name</Label>
                      <Input
                        value={reportMeta.valuer_name || ''}
                        onChange={(e) => setReportMeta(prev => ({ ...prev, valuer_name: e.target.value }))}
                        data-testid="input-valuer-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Qualification</Label>
                      <Select
                        value={reportMeta.valuer_qualification || 'MRICS'}
                        onValueChange={(value) => setReportMeta(prev => ({ ...prev, valuer_qualification: value }))}
                      >
                        <SelectTrigger data-testid="select-qualification">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="MRICS">MRICS</SelectItem>
                          <SelectItem value="FRICS">FRICS</SelectItem>
                          <SelectItem value="AssocRICS">AssocRICS</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Company</Label>
                      <Input
                        value={reportMeta.valuer_company || ''}
                        onChange={(e) => setReportMeta(prev => ({ ...prev, valuer_company: e.target.value }))}
                        data-testid="input-valuer-company"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>RICS Number</Label>
                      <Input
                        value={reportMeta.rics_number || ''}
                        onChange={(e) => setReportMeta(prev => ({ ...prev, rics_number: e.target.value }))}
                        data-testid="input-rics-number"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Toggles */}
              <Card>
                <CardHeader>
                  <CardTitle>Report Options</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                    <div>
                      <Label className="text-base font-semibold">CPR 35 Mode</Label>
                      <p className="text-sm text-slate-600">Include expert witness sections and Statement of Truth</p>
                    </div>
                    <Switch
                      checked={toggles.cpr35_mode}
                      onCheckedChange={(checked) => setToggles(prev => ({ ...prev, cpr35_mode: checked }))}
                      data-testid="toggle-cpr35"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                    <div>
                      <Label className="text-base font-semibold">Acquisition Mode</Label>
                      <p className="text-sm text-slate-600">Include SDLT and purchase fees in calculations</p>
                    </div>
                    <Switch
                      checked={toggles.acquisition_mode}
                      onCheckedChange={(checked) => setToggles(prev => ({ ...prev, acquisition_mode: checked }))}
                      data-testid="toggle-acquisition"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Export Buttons */}
              <Card>
                <CardHeader>
                  <CardTitle>Generate Report</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4">
                    <Button 
                      onClick={() => handleGenerateReport('docx')}
                      className="flex-1"
                      data-testid="generate-docx-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download DOCX
                    </Button>
                    <Button 
                      onClick={() => handleGenerateReport('pdf')}
                      variant="outline"
                      className="flex-1"
                      data-testid="generate-pdf-btn"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
